<template>
  <v-container>
    <template>
   <CRUDList
      ref="crudList"
      :canAdd="canAddToSharedList"
      :canEdit="true"
      :canSave="canEditSharedList"
      :showSelect="true"
      :useSelectedItemsStore="true"
      :enableFixedHeaders="true"
      :displayTopPagination="false"
      :canDeleteInDialog="true"
      :headers="headers"
      :baseCrudKey="baseCrudKey"
      :dialogHeaderName="dialogHeaderName"
      :defaultSortBy="defaultSortBy"
      :tableTitle="dialogHeaderName"
      dialogDynamicWidth="60vw"
      tableWrapperDynamicClassname='elevation-3 ma-3 pa-1 taxroll-table-wrapper'
      tableDynamicClassname='taxroll-custom-table'
      tableDynamicHeight='450'
      @upsertItem="upsertRecords"
      @deleteItem="deleteFromDiaglog"
      @loadCRUDList="handleMtgCodesLoad"
      :loadingData="gridLoading.flag"
      :loadingText="gridLoading.text"
    >
    <template v-slot:[`additionalActionsTop`]>
      <div class="d-flex flex-wrap align-center">
        <v-btn color="primary" rounded dense small @click="printMtgCodesReport"
        class="mt-3 mr-2">
          Mortgage Company List
        </v-btn>
        <v-btn color="primary" rounded dense small @click="printAddressLabelsReport"
        class="mt-3 mr-2">
          {{ addressLabelsText }}
        </v-btn>
        <v-btn color="primary" outlined rounded dense small @click="printStatementsLabelsReport"
        class="mt-3 mr-2">
          Statements Labels
        </v-btn>
        <v-btn text rounded dense small :disabled="!selectedIds.length" @click="clearSelection"
        class="mt-3 mr-2">
          Clear
        </v-btn>
        <v-text-field class="input-text-size mt-3" style="min-width: 260px; flex: 1;" label="Wildcard Search
        (Mortgage Code, Company Name, Contact Person, Address, Email Address)"
        v-model="criteriaState.mtgCodeWildcard"
        @input="searchByTimeOut" />
      </div>
    </template>
    <template v-slot:[`editedItem`]="{}">
      <taxMortgageCodesDialog :baseCrudKey="baseCrudKey"/>
    </template>
    </CRUDList>
    </template>
  </v-container>
</template>

<script>
import {
  mapMutations, mapActions, mapGetters,
} from 'vuex';
import CRUDList from '../../common/base/crud-2.0/CRUDList.vue';
import { MortgageCodesHeader } from '../Utils/taxHeaders.utils';
import taxMortgageCodesDialog from './taxMortgageCodesDialog.vue';
import { defaultGridLoading, manageGridLoading } from '../../../util/shared/vue-global';
import SharedListAccessControlMixin from '../SharedListAccessControl.mixin';
import { generateQueryString } from '../../common/base/report/report-util';
import { clients } from '../../../util/clients';
import '../../../assets/style.css';
import { SEARCH_DEBOUNCE_TIMER } from '../../tax/common-utils/constants';

const { backendRest } = clients.direct;
export default {
  name: 'taxMortgageCodesList',
  mixins: [SharedListAccessControlMixin],
  components: {
    CRUDList,
    taxMortgageCodesDialog,
  },
  data() {
    return {
      selectedRows: [],
      baseCrudKey: 'taxMortgageCodesList',
      selectedRow: null,
      dialogHeaderName: 'Mortgage Codes',
      headers: MortgageCodesHeader,
      gridLoading: defaultGridLoading(),
      delayTimerId: null,
    };
  },
  created() {
    // The selection lives in the store; start clean so labels never print a forgotten pick.
    this.clearSelection();
    manageGridLoading(this, true, 'Searching...');
  },
  destroyed() {
  },
  computed: {
    ...mapGetters('base/crud', [
      'criteria',
      'index',
      'selectedItemsOfAllPages',
    ]),
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    selectedIds() {
      return (this.selectedItemsOfAllPages(this.baseCrudKey) || []).map((item) => item._id).filter(Boolean);
    },
    addressLabelsText() {
      const count = this.selectedIds.length;
      return count ? `Address Labels (${count} selected)` : 'Address Labels (All)';
    },
    defaultSortBy() {
      return [
        {
          column: 'mtgCode',
          direction: 'asc',
        },
      ];
    },
  },
  methods: {
    manageGridLoading,
    ...mapActions(['reAuth']),
    ...mapActions('shared/taxMortgageCodesShared', [
      'loadTaxMortgageCodesSharedRecords',
      'upserttaxMortgageCodes',
      'deleteMortgageCodes',
      'findMortgageCodesByCode',
    ]),
    ...mapMutations('base/crud', [
      'setCriteria',
      'setItem',
      'setIsVisible',
      'setIndex',
      'setSelectedItemsOfAllPages',
    ]),
    clearSelection() {
      this.setSelectedItemsOfAllPages([this.baseCrudKey, []]);
    },
    async handleMtgCodesLoad() {
      const { baseCrudKey, criteriaState } = this;
      const criteria = { ...criteriaState };
      this.loadTaxMortgageCodesSharedRecords({ criteria, baseCrudKey }).finally(() => {
        manageGridLoading(this, false);
      });
    },
    // Pottawatomie keyed Milburn Acreages 00834 twice because the first one could not be found,
    // and nothing stopped the second. Warn - do not block, a county may genuinely want two.
    async duplicateCodeWarning(item) {
      const mtgCode = (item.mtgCode || '').trim();
      if (!mtgCode) return '';
      const existing = await this.findMortgageCodesByCode({ mtgCode });
      const others = (existing || []).filter((e) => e._id !== item._id);
      if (!others.length) return '';
      const names = others.map((o) => o.mtgCoName || '(no name)').join(', ');
      return `Mortgage code ${mtgCode} is already used by ${names}.\n\n`
        + 'Save this record anyway?';
    },
    async upsertRecords(item) {
      const { baseCrudKey } = this;
      const updatedItem = { ...item };
      const editedIndex = this.index(baseCrudKey);
      const warning = await this.duplicateCodeWarning(updatedItem);
      if (warning && !window.confirm(warning)) {
        // The dialog closes itself as soon as it emits, so put the record back for editing.
        this.setItem([baseCrudKey, updatedItem]);
        this.setIndex([baseCrudKey, editedIndex]);
        this.setIsVisible([baseCrudKey, true]);
        return;
      }
      await this.upserttaxMortgageCodes({ item: updatedItem, baseCrudKey });
    },
    deleteFromDiaglog(item) {
      if (window.confirm('Are you sure you want to delete this item?')) {
        const { baseCrudKey } = this;
        this.deleteMortgageCodes({ item, baseCrudKey });
      }
    },
    async printMtgCodesReport() {
      const { jwt } = await this.reAuth();
      let sortBy = 'mtgCode';
      let direction = 'asc';
      if ((this.criteriaState || {}).sortBy && this.criteriaState.sortBy.length) {
        sortBy = this.criteriaState.sortBy[0].column;
        direction = this.criteriaState.sortBy[0].direction;
      }
      const params = [
        `token=${jwt}`,
        `sortBy=${sortBy || ''}`,
        `direction=${direction || ''}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/shared/mortgage-codes-report${queryString}`;
      window.open(url, '_blank');
    },
    // Tulsa mails statements only to the codes flagged for statements; the grid is paged, so
    // the server picks those codes rather than the rows on screen.
    async printStatementsLabelsReport() {
      const { jwt } = await this.reAuth();
      const queryString = generateQueryString([`token=${jwt}`, 'statementsOnly=true']);
      const url = `${backendRest.defaults.baseURL}/shared/mortgage-address-labels-report${queryString}`;
      window.open(url, '_blank');
    },
    async printAddressLabelsReport() {
      const { jwt } = await this.reAuth();
      // With no codes selected the report prints every code, as it always did.
      const params = [
        `token=${jwt}`,
        `ids=${this.selectedIds.join(',')}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/shared/mortgage-address-labels-report${queryString}`;
      window.open(url, '_blank');
    },
    searchByTimeOut() {
      clearTimeout(this.delayTimerId);
      this.delayTimerId = setTimeout(() => {
        this.handleMtgCodesLoad();
      }, SEARCH_DEBOUNCE_TIMER);
    },
  },
};
</script>
